import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { meshtasticSectionLabel, MESHTASTIC_SECTION_ORDER } from '../lib/definitions/settings';
import { NO_CONNECTIONS_EMPTY_STATE } from '../lib/definitions/navigation';
import { RadioControlOperationCard } from "./RadioControlOperationCard";
import { RadioTransportPanel } from "./RadioSnapshotView";
import type {
  RadioConfigEntry,
  RadioControlCapabilitiesResponse,
  RadioControlOperation,
  RadioControlParamSpec,
  RadioControlTransportCapabilities,
} from "../api/types";

// The operations surface (task brief item 3 / RADIO_CONTROL_CONTRACT.md
// section 5): GET /api/radio/control/capabilities lists every registered
// operation per transport, and this renders every single one of them —
// including unsupported and RF-blocked ones — grouped by rf_class so the
// boundary between "controls our own radio" and "puts something on the
// air" is structurally obvious rather than a label an operator has to read
// carefully. Nothing here hardcodes an operation id or list; a new backend
// operation appears automatically the next time this fetches.
//
// This endpoint does not exist yet as of this writing (built concurrently
// by another agent against the same contract) — tryApiGet's 404 -> null
// path, extended here to ANY fetch failure, degrades to a plain
// "radio control API unavailable" note rather than a crash or a stuck
// loading spinner.

const GROUP_ORDER: { rfClass: string; title: string }[] = [
  { rfClass: "local", title: "Local device control" },
  { rfClass: "local_rf_side_effect", title: "Local control with a broadcast side effect" },
  { rfClass: "rf", title: "Radio-frequency operations" },
];

// --- Node Configuration category grouping ------------------------------
// Within each rf_class group above (the RF safety boundary — never
// flattened away), operation cards are further grouped by the backend's
// own `category` field, mirroring the official Meshtastic app/web client's
// Radio Configuration / Module Configuration split (and MeshMonitor's own
// mirror of it — see src/components/search/configSections.ts:210-212 in
// that sibling project). Nothing here hardcodes an operation id: category
// is read straight from the API, same discipline as rf_class/label/summary
// elsewhere in this file.
const CATEGORY_ORDER: readonly string[] = [
  "Radio Configuration",
  "Module Configuration",
  "Channels",
  "Device",
  "Node database",
  "Mesh operations",
  "Remote administration",
];

/** "" for an absent/blank category — never invent a label for it. */
function categoryOf(op: RadioControlOperation): string {
  return op.category && op.category.trim() ? op.category : "";
}

/** CATEGORY_ORDER position, or CATEGORY_ORDER.length (sorts after every listed category) for one it doesn't list. */
function categoryRank(cat: string): number {
  const idx = CATEGORY_ORDER.indexOf(cat);
  return idx === -1 ? CATEGORY_ORDER.length : idx;
}

interface CategorySubgroup {
  // "" = the final uncategorised run — rendered with no subheading.
  category: string;
  items: RenderItem[];
  // This category's slice of the parent group's expandedOps — which now
  // only ever contains ops that produced MULTIPLE cards (see
  // computeGroupItems below) — same first-appearance order as expandedOps
  // itself. Keeps each such op's summary/rf_note note sitting with the
  // category its cards landed in, instead of all notes floating above the
  // whole rf_class group. An op with exactly one card never appears here;
  // its copy lives on the card itself instead.
  notesOps: RadioControlOperation[];
}

// Splits one rf_class group's already-ordered `items` (expanded cards by
// MESHTASTIC_SECTION_ORDER, then plain ops in original order — computeGroupItems'
// own ordering, untouched here) into per-category runs, preserving that
// relative order within each category. Category run order: CATEGORY_ORDER's
// fixed sequence first, then any category not in that list (first-appearance
// order), then the uncategorised run last, always.
function computeCategorySubgroups(g: TransportGroup): CategorySubgroup[] {
  const buckets = new Map<string, RenderItem[]>();
  const uncategorised: RenderItem[] = [];

  for (const item of g.items) {
    const cat = categoryOf(item.op);
    if (!cat) {
      uncategorised.push(item);
      continue;
    }
    const bucket = buckets.get(cat);
    if (bucket) bucket.push(item);
    else buckets.set(cat, [item]);
  }

  // Map iteration order is insertion (= first-appearance) order — stable
  // sort by categoryRank keeps that order intact among ties (every category
  // not in CATEGORY_ORDER shares rank CATEGORY_ORDER.length).
  const orderedCategories = [...buckets.keys()].sort((a, b) => categoryRank(a) - categoryRank(b));

  const subgroups: CategorySubgroup[] = orderedCategories.map((category) => ({
    category,
    items: buckets.get(category)!,
    notesOps: g.expandedOps.filter((op) => categoryOf(op) === category),
  }));

  if (uncategorised.length > 0) {
    subgroups.push({
      category: "",
      items: uncategorised,
      notesOps: g.expandedOps.filter((op) => categoryOf(op) === ""),
    });
  }

  return subgroups;
}

// --- Node Configuration section expansion -----------------------------
// Settings > Node Configuration used to hide every Meshtastic config field
// behind a required "Section -> (choose...)" dropdown on a single card, so
// arriving at the page showed no editable fields at all. This expands any
// write operation shaped like "pick one section/module/channel, then edit
// its values" into one always-visible card per choice — matching
// MeshMonitor's ConfigurationTab.tsx, which renders every config section
// stacked with no chooser (the reference for this surface; see
// RADIO_CONTROL_CONTRACT.md and this file's own header comment above for
// why nothing here may hardcode an operation id, label, or rf_class).
//
// Detected purely by PARAM SHAPE, never by op.id: an operation expands only
// when it has EXACTLY two params, one named "values" of type "object", and
// the other either a non-empty-choices "section"/"module" param, or an
// "index" param of type "int". Everything else — including the 3-param
// remote-config operation (server picks a target node AND a section) and
// the 3-param MeshCore channel operation — falls through unexpanded, one
// card with its own form, exactly as before.
function detectExpansion(
  op: RadioControlOperation,
): { keyParam: RadioControlParamSpec; kind: "choices" } | { keyParam: RadioControlParamSpec; kind: "index" } | null {
  const params = Array.isArray(op.params) ? op.params : [];
  if (params.length !== 2) return null;
  const valuesParam = params.find((p) => p.name === "values" && p.type === "object");
  if (!valuesParam) return null;
  const other = params.find((p) => p !== valuesParam);
  if (!other) return null;
  if ((other.name === "section" || other.name === "module") && (other.choices?.length ?? 0) > 0) {
    return { keyParam: other, kind: "choices" };
  }
  if (other.name === "index" && other.type === "int") {
    return { keyParam: other, kind: "index" };
  }
  return null;
}

/** Anchor ids end up as DOM element ids and CSS selector targets — keep them to a plain slug. */
function toAnchorId(raw: string): string {
  return raw.replace(/[^A-Za-z0-9_-]/g, "-");
}

/** MESHTASTIC_SECTION_ORDER position, or MESHTASTIC_SECTION_ORDER.length (a finite sentinel that sorts after every listed key) for a key it doesn't list. */
function orderIndex(key: string): number {
  const idx = MESHTASTIC_SECTION_ORDER.indexOf(key);
  return idx === -1 ? MESHTASTIC_SECTION_ORDER.length : idx;
}

interface ExpandedItem {
  kind: "expanded";
  op: RadioControlOperation;
  key: string;
  fixedParams: Record<string, unknown>;
  title: string;
  anchorId: string;
  // True when this item's op produced MORE THAN ONE card in this group
  // (e.g. set_config expanded into one card per Meshtastic section) — the
  // actual condition for whether the group renders this op's summary/
  // rf_note once at group level instead of on the card itself. NOT the
  // same question as "does this card have fixedParams": NodeCommandsSection
  // also pre-binds fixedParams (the viewed node) but always produces
  // exactly one card per operation, so its cards must show their own copy
  // — see RadioControlOperationCard's hideOwnCopy prop.
  multiCard: boolean;
}

interface PlainOpItem {
  kind: "op";
  op: RadioControlOperation;
}

type RenderItem = ExpandedItem | PlainOpItem;

// Expands every expandable op in `ops` (already filtered to one rf_class
// group) into its per-choice cards, sorts the expanded cards by
// MESHTASTIC_SECTION_ORDER (keys the order list doesn't cover keep their
// original `choices` order, appended after the ones it does), and appends
// every unexpanded op card after that, in its original relative order.
function computeGroupItems(
  ops: RadioControlOperation[],
  transportId: string,
  entries: RadioConfigEntry[] | null,
): { items: RenderItem[]; expandedOps: RadioControlOperation[] } {
  const expanded: ExpandedItem[] = [];
  const plain: PlainOpItem[] = [];
  // Every op that actually produced at least one expanded card in this
  // group, in first-appearance (= `ops`) order — used by TransportPanel to
  // render that op's summary/rf_note exactly once per group instead of
  // once per card (see this file's Fix-1 note below).
  const expandedOps: RadioControlOperation[] = [];

  for (const op of ops) {
    const expansion = detectExpansion(op);
    if (!expansion) {
      plain.push({ kind: "op", op });
      continue;
    }

    if (expansion.kind === "choices") {
      const choices = expansion.keyParam.choices ?? [];
      // Only a choice list with more than one entry actually produces
      // multiple cards sharing one description — a single-choice op still
      // gets exactly one card, so its own summary/rf_note belongs on that
      // card, not promoted to a group-level note (same rule as the "index"
      // branch below).
      const multiCard = choices.length > 1;
      for (const choice of choices) {
        expanded.push({
          kind: "expanded",
          op,
          key: choice,
          fixedParams: { [expansion.keyParam.name]: choice },
          title: meshtasticSectionLabel(choice),
          anchorId: toAnchorId(`nodecfg-${transportId}-${expansion.keyParam.name}-${choice}`),
          multiCard,
        });
      }
      if (multiCard) expandedOps.push(op);
      continue;
    }

    // kind === "index": the choice list is the channel indices actually
    // present in the live snapshot, never a fabricated fixed-size list.
    const channels = entries?.find((e) => e.transport_id === transportId)?.channels;
    if (Array.isArray(channels) && channels.length > 0) {
      const multiCard = channels.length > 1;
      channels.forEach((_, idx) => {
        expanded.push({
          kind: "expanded",
          op,
          key: String(idx),
          fixedParams: { [expansion.keyParam.name]: idx },
          title: `Channel ${idx}`,
          anchorId: toAnchorId(`nodecfg-${transportId}-${expansion.keyParam.name}-${idx}`),
          multiCard,
        });
      });
      if (multiCard) expandedOps.push(op);
    } else {
      // No live snapshot to enumerate real channel indices from — fall
      // back to the unexpanded card exactly as before, no commentary.
      plain.push({ kind: "op", op });
    }
  }

  const expandedOrder = new Map<ExpandedItem, number>();
  expanded.forEach((item, i) => expandedOrder.set(item, i));
  expanded.sort((a, b) => {
    const rankDiff = orderIndex(a.key) - orderIndex(b.key);
    if (rankDiff !== 0) return rankDiff;
    return (expandedOrder.get(a) ?? 0) - (expandedOrder.get(b) ?? 0);
  });
  return { items: [...expanded, ...plain], expandedOps };
}

interface TransportGroup {
  rfClass: string;
  title: string;
  items: RenderItem[];
  // Ops that produced MULTIPLE cards in this group (not merely "expanded
  // at all" — see computeGroupItems' multiCard flag), first-appearance
  // order. TransportPanel renders each one's summary/rf_note once here,
  // above its cards, instead of on every card; an op with only one card
  // never appears here — its copy shows on that card instead.
  expandedOps: RadioControlOperation[];
}

// Single source of truth for both what TransportPanel renders and what
// section-nav chips RadioConfigSection shows — computed once per transport
// here, in RadioControlPanel, rather than re-detected inside TransportPanel.
function computeTransportGroups(transport: RadioControlTransportCapabilities, entries: RadioConfigEntry[] | null): TransportGroup[] {
  const operations = Array.isArray(transport.operations) ? transport.operations : [];
  const knownClasses = new Set(GROUP_ORDER.map((g) => g.rfClass));

  const groups: TransportGroup[] = GROUP_ORDER.map((g) => {
    const { items, expandedOps } = computeGroupItems(
      operations.filter((o) => o.rf_class === g.rfClass),
      transport.transport_id,
      entries,
    );
    return { rfClass: g.rfClass, title: g.title, items, expandedOps };
  });

  const otherOps = operations.filter((o) => !knownClasses.has(o.rf_class));
  if (otherOps.length > 0) {
    const { items, expandedOps } = computeGroupItems(otherOps, transport.transport_id, entries);
    groups.push({ rfClass: "other", title: "Other operations", items, expandedOps });
  }

  return groups.filter((g) => g.items.length > 0);
}

function TransportPanel({
  transport,
  groups,
  entries,
  snapshotEntries,
}: {
  transport: RadioControlTransportCapabilities;
  groups: TransportGroup[];
  entries: RadioConfigEntry[] | null;
  // Raw, unnarrowed /api/radio/config state — only for this transport's own
  // Live snapshot block below (see RadioControlPanel's own comment on why
  // it keeps this separate from `entries`, which stays the resolved
  // array-or-null form used for seeding).
  snapshotEntries: RadioConfigEntry[] | null | "unavailable";
}) {
  const operations = Array.isArray(transport.operations) ? transport.operations : [];

  // This transport's own chip row — one chip per expanded section/channel
  // card in its groups, scoped to just this transport so the label is the
  // section name alone ("Device", "LoRa", "Channel 0"), no transport
  // prefix, since the row already sits under this transport's own heading.
  // A transport with no expanded items (e.g. MeshCore) renders no row.
  const chips = useMemo(
    () =>
      groups.flatMap((g) =>
        g.items
          .filter((item): item is ExpandedItem => item.kind === "expanded")
          .map((item) => ({ id: item.anchorId, label: item.title })),
      ),
    [groups],
  );

  // Every operation card on this tab starts collapsed (task: a tab opens as
  // a scannable list of headers, not ~22 full forms) — tracked here by card
  // id (an expanded item's anchorId, or a plain item's op.id; both are
  // already this list's own React `key`s below, and are only ever unique
  // within this one transport's cards, which is all this state needs).
  // Scoped to one TransportPanel instance per transport: switching to a
  // different transport's tab mounts a fresh TransportPanel and this starts
  // over collapsed, matching how this page's other tabbed sections already
  // discard ephemeral state on tab switch (see SettingsPage.tsx's
  // EditableSection, unmounted the same way when its own tab changes).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  function toggleCard(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // A chip must expand its card before scrolling to it — a collapsed card
  // has no visible height to scroll to. The scroll is deferred one frame so
  // it runs after React has committed the expand, otherwise it would
  // measure the card's still-collapsed position.
  function jumpToChip(id: string) {
    setExpandedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  // Live snapshot — moved here from RadioConfigSection.tsx (was one block
  // covering every transport, at the bottom of the whole section); now one
  // per transport, at the bottom of that transport's own tab, filtered to
  // this transport's entry only. Starts collapsed, same as before.
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const snapshotEntry = Array.isArray(snapshotEntries)
    ? snapshotEntries.find((e) => e.transport_id === transport.transport_id)
    : undefined;

  return (
    <div className="radio-control-transport">
      <div className="radio-transport-header">
        <h4>{transport.label || transport.transport_id}</h4>
        <span className="badge" data-connected={transport.connected ? "true" : "false"}>
          {transport.connected ? "Connected" : "Not connected"}
        </span>
      </div>

      {chips.length > 0 && (
        <nav className="radio-control-section-nav" aria-label="Jump to a configuration section">
          {chips.map((s) => (
            <button key={s.id} type="button" className="radio-control-section-nav-chip" onClick={() => jumpToChip(s.id)}>
              {s.label}
            </button>
          ))}
        </nav>
      )}

      {operations.length === 0 && <div className="empty-state">This transport reports no control operations.</div>}

      {groups.map((g) => (
        <div className="radio-control-group" key={g.rfClass}>
          <h5 className="radio-control-group-title">{g.title}</h5>
          {computeCategorySubgroups(g).map((sub, subIdx) => (
            <div className="radio-control-category-group" key={sub.category || `uncategorised-${subIdx}`}>
              {sub.category && <h6 className="radio-control-category-title">{sub.category}</h6>}
              {sub.notesOps.length > 0 && (
                <div className="radio-control-group-notes">
                  {sub.notesOps.map((op) => (
                    <div className="radio-control-group-note" key={op.id}>
                      <span className="radio-control-group-note-label">{op.label}</span>
                      {op.summary && <p className="settings-field-desc">{op.summary}</p>}
                      {op.rf_note && (
                        <p className="radio-op-rf-note" data-rf-class={op.rf_class}>
                          {op.rf_note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {sub.items.map((item) =>
                item.kind === "expanded" ? (
                  <RadioControlOperationCard
                    key={item.anchorId}
                    transportId={transport.transport_id}
                    op={item.op}
                    entries={entries}
                    fixedParams={item.fixedParams}
                    titleOverride={item.title}
                    anchorId={item.anchorId}
                    hideOwnCopy={item.multiCard}
                    expanded={expandedIds.has(item.anchorId)}
                    onToggle={() => toggleCard(item.anchorId)}
                  />
                ) : (
                  <RadioControlOperationCard
                    key={item.op.id}
                    transportId={transport.transport_id}
                    op={item.op}
                    entries={entries}
                    expanded={expandedIds.has(item.op.id)}
                    onToggle={() => toggleCard(item.op.id)}
                  />
                ),
              )}
            </div>
          ))}
        </div>
      ))}

      <div className="node-detail-section">
        <div className="settings-section-header" onClick={() => setSnapshotOpen((o) => !o)}>
          <h3>Live snapshot</h3>
          <span>{snapshotOpen ? "▲" : "▼"}</span>
        </div>
        {snapshotOpen && (
          <>
            <p className="settings-field-desc">
              This transport's own reported node configuration — identity, position, device and module settings, and
              channels.
            </p>
            {snapshotEntries === null && <div className="empty-state">Loading…</div>}
            {snapshotEntries === "unavailable" && <div className="node-detail-note">Couldn't load the radio configuration.</div>}
            {Array.isArray(snapshotEntries) &&
              (snapshotEntry ? (
                <RadioTransportPanel entry={snapshotEntry} />
              ) : (
                <div className="node-detail-note">
                  No live snapshot reported for this transport — a real gap, not an empty table.
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}

export function RadioControlPanel({
  entries,
}: {
  // Full, unnarrowed /api/radio/config state (RadioConfigSection.tsx's own
  // fetch, kept keyed only to that section's opening — never to anything
  // in here). Never pre-narrow this before passing it down.
  entries: RadioConfigEntry[] | null | "unavailable";
}) {
  // "disabled" = the endpoint 404'd, meaning the radio_control_api feature
  // itself is off (its route is never registered) — the expected state on
  // a fresh install with nothing configured yet, not a failure. "error"
  // means the request actually failed (network/5xx) — a real problem,
  // shown distinctly rather than folded into the same message.
  const [capabilities, setCapabilities] = useState<RadioControlCapabilitiesResponse | null | "disabled" | "error">(null);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<RadioControlCapabilitiesResponse>("/api/radio/control/capabilities")
      .then((res) => !cancelled && setCapabilities(res ?? "disabled"))
      .catch(() => !cancelled && setCapabilities("error"));
    return () => {
      cancelled = true;
    };
  }, []);

  const transports = useMemo(
    () => (capabilities && capabilities !== "disabled" && capabilities !== "error" && Array.isArray(capabilities.transports) ? capabilities.transports : []),
    [capabilities],
  );

  // Seeding/enumeration (config forms, channel-index expansion) only ever
  // wants the resolved array or null, exactly as this collapsed `entries`
  // before this change — "disabled"/"error" both read as "no snapshot to
  // seed from" here, same as before. The raw three-state `entries` above
  // is threaded through separately, unchanged, to each TransportPanel's
  // own Live snapshot block, which does need to tell "loading" apart from
  // "unavailable".
  const seedEntries = Array.isArray(entries) ? entries : null;

  const transportGroups = useMemo(
    () => transports.map((t) => ({ transport: t, groups: computeTransportGroups(t, seedEntries) })),
    [transports, seedEntries],
  );

  // One tab per transport (Settings > Node Configuration > Radio control).
  // Defaults to the first transport in the list; falls back to the first
  // one again if the selected transport disappears from a later
  // capabilities response. Always shown once there's at least one
  // transport, even just the one — no special-casing a single transport
  // away.
  const [selectedTransportId, setSelectedTransportId] = useState<string | null>(null);
  useEffect(() => {
    if (transports.length === 0) return;
    if (!selectedTransportId || !transports.some((t) => t.transport_id === selectedTransportId)) {
      setSelectedTransportId(transports[0].transport_id);
    }
  }, [transports, selectedTransportId]);

  if (capabilities === null) return <div className="empty-state">Loading…</div>;
  if (capabilities === "disabled") {
    return (
      <div className="empty-state">
        Radio control is off on this instance. {NO_CONNECTIONS_EMPTY_STATE} Then turn on the Radio Control feature to
        read/write that radio's configuration.
      </div>
    );
  }
  if (capabilities === "error") {
    return <div className="node-detail-note">Radio control is unavailable right now.</div>;
  }

  return (
    <div className="radio-control-panel">
      <div className="radio-control-guard-status">
        <span className="badge" data-connected={capabilities.transmit_allowed ? "true" : "false"}>
          {capabilities.transmit_allowed ? "Live transmit enabled" : "Live transmit disabled"}
        </span>
        {!capabilities.transmit_allowed && capabilities.transmit_blocked_reason && (
          <span className="settings-field-desc">{capabilities.transmit_blocked_reason}</span>
        )}
      </div>

      {capabilities.shared_link_warning && (
        <div className="radio-control-shared-link-warning">
          <strong>Shared link.</strong>
          <p>{capabilities.shared_link_warning}</p>
        </div>
      )}

      {transportGroups.length === 0 ? (
        <div className="empty-state">No controllable transports configured.</div>
      ) : (
        <>
          {/* Same tab-strip markup/classes as SettingsPage.tsx's own
              .settings-tabs, so this reads as native to the page — one tab
              per connected radio, replacing the old vertical stack of every
              transport's panel at once. */}
          <div className="settings-tabs">
            {transportGroups.map(({ transport }) => (
              <button
                key={transport.transport_id}
                type="button"
                className="settings-tab"
                data-active={selectedTransportId === transport.transport_id ? "true" : "false"}
                onClick={() => setSelectedTransportId(transport.transport_id)}
              >
                {transport.label || transport.transport_id}
              </button>
            ))}
          </div>

          {transportGroups
            .filter(({ transport }) => transport.transport_id === selectedTransportId)
            .map(({ transport, groups }) => (
              <TransportPanel
                key={transport.transport_id}
                transport={transport}
                groups={groups}
                entries={seedEntries}
                snapshotEntries={entries}
              />
            ))}
        </>
      )}
    </div>
  );
}
